import type { DocNode } from '@atlaskit/adf-schema/doc';

export type PageRemixLifecycleAdmissionRequest = {
	conversationId?: string;
};

export type ObjectSidebarPageRemixStartRejectionReason =
	| 'adf-load-failed'
	| 'duplicate-submit'
	| 'empty-adf'
	| 'lifecycle-rejected'
	| 'malformed-adf'
	| 'missing-identity'
	| 'not-armed'
	| 'stale-adf'
	| 'stale-open-action'
	| 'wrong-scope';

export type ObjectSidebarPageRemixStartResult =
	| {
			status: 'started';
			adfDocument: DocNode;
	  }
	| {
			status: 'rejected';
			reason: ObjectSidebarPageRemixStartRejectionReason;
	  };

type ObjectSidebarPageRemixStartOptions = {
	resolveAdfDocument: () => Promise<DocNode | null>;
	admitLifecycle?: (request: PageRemixLifecycleAdmissionRequest) => boolean;
	conversationId?: string;
	isCurrent?: () => boolean;
	isOpenActionCurrent?: () => boolean;
	onRejected: (reason: ObjectSidebarPageRemixStartRejectionReason) => void;
};

type UnknownAdfNode = {
	type: string;
	text?: string;
	content?: UnknownAdfNode[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

// Validate with the same options the renderer uses (`editor-common` `validateADFEntity`):
// `stage0`, because Confluence renders pages with the stage-0 ADF schema (see
// `adf-renderer/ADFRendererComponent`) and without it the validator declines stage-0 specs such as
// `layoutSection_with_single_column` (1 to 5 columns, optional column divider); and
// `allowPrivateAttributes`, because Confluence page ADF carries private `__` attributes such as
// `__confluenceMetadata` on internal page links and `__expanded` on expands, which strict validation
// otherwise reports as redundant. Either omission rejects a page the product already renders as
// `malformed-adf`. Structural checks (root type, version, array content) stay in the caller; this only
// decides whether the nodes are valid ADF.
const isSchemaValidAdf = async (value: DocNode): Promise<boolean> => {
	try {
		const { validator } = await import(
			/* webpackChunkName: "loadable-adf-utils-validator" */ '@atlaskit/adf-utils/validator'
		);
		return validator(undefined, undefined, { stage0: true, allowPrivateAttributes: true })(value)
			.valid;
	} catch {
		return false;
	}
};

const hasMeaningfulAdfContent = (node: UnknownAdfNode): boolean => {
	if (node.type === 'text') {
		return typeof node.text === 'string' && node.text.trim().length > 0;
	}
	if (!Array.isArray(node.content)) {
		return node.type !== 'paragraph' && node.type !== 'doc';
	}
	if (node.type !== 'paragraph' && node.type !== 'doc') {
		return true;
	}
	return node.content.some(hasMeaningfulAdfContent);
};

const rejectStart = (
	reason: ObjectSidebarPageRemixStartRejectionReason,
	onRejected: ObjectSidebarPageRemixStartOptions['onRejected'],
): ObjectSidebarPageRemixStartResult => {
	onRejected(reason);
	return { status: 'rejected', reason };
};

export const prepareObjectSidebarPageRemixStart = async ({
	resolveAdfDocument,
	admitLifecycle,
	conversationId,
	isCurrent,
	isOpenActionCurrent,
	onRejected,
}: ObjectSidebarPageRemixStartOptions): Promise<ObjectSidebarPageRemixStartResult> => {
	let resolvedAdf: DocNode | null = null;
	try {
		resolvedAdf = await resolveAdfDocument();
	} catch {
		return rejectStart('adf-load-failed', onRejected);
	}

	if (isOpenActionCurrent && !isOpenActionCurrent()) {
		return rejectStart('stale-open-action', onRejected);
	}

	if (isCurrent && !isCurrent()) {
		return rejectStart('stale-adf', onRejected);
	}

	if (!resolvedAdf) {
		return rejectStart('empty-adf', onRejected);
	}

	if (
		!isRecord(resolvedAdf) ||
		resolvedAdf.type !== 'doc' ||
		resolvedAdf.version !== 1 ||
		!Array.isArray(resolvedAdf.content) ||
		!(await isSchemaValidAdf(resolvedAdf))
	) {
		return rejectStart('malformed-adf', onRejected);
	}

	if (!resolvedAdf.content.some((node) => hasMeaningfulAdfContent(node as UnknownAdfNode))) {
		return rejectStart('empty-adf', onRejected);
	}

	try {
		if (admitLifecycle && !admitLifecycle({ conversationId })) {
			return rejectStart('lifecycle-rejected', onRejected);
		}
	} catch {
		return rejectStart('lifecycle-rejected', onRejected);
	}

	return { status: 'started', adfDocument: resolvedAdf };
};

type ObjectSidebarPageRemixNativeStartOptions = {
	startNativeRemix: () => Promise<void>;
	onNativeStartRejected: () => void;
};

export const runObjectSidebarPageRemixNativeStart = async ({
	startNativeRemix,
	onNativeStartRejected,
}: ObjectSidebarPageRemixNativeStartOptions): Promise<void> => {
	try {
		await startNativeRemix();
	} catch {
		onNativeStartRejected();
		throw new Error('Failed to start page remix from Rovo submit');
	}
};
